import React, { useEffect, useState } from 'react';
import { adminApi } from '../../api';
import Loading from '../../components/common/Loading';

interface AdminUser {
  id: number; email: string; name: string; phone: string | null;
  role: string; status: string; grade: string; createdAt: string;
}

const STATUS_LABEL: Record<string, string> = {
  ACTIVE: '활성', SUSPENDED: '정지', DELETED: '탈퇴',
};

const GRADE_LABEL: Record<string, string> = {
  NORMAL: '일반', BLACK: '블랙',
};

const ROLE_LABEL: Record<string, string> = {
  ADMIN: '관리자', USER: '일반회원',
};

const AdminUsers: React.FC = () => {
  const [users, setUsers] = useState<AdminUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  const load = () => {
    setLoading(true);
    adminApi.getUsers(page).then(r => {
      setUsers(r.data.data.content);
      setTotalPages(r.data.data.totalPages);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [page]);

  const handleStatus = async (id: number, status: string, label: string) => {
    if (!window.confirm(`${label} 처리하시겠습니까?`)) return;
    await adminApi.changeUserStatus(id, status);
    load();
  };

  const handleGrade = async (id: number, grade: string, label: string) => {
    if (!window.confirm(`${label} 처리하시겠습니까?`)) return;
    await adminApi.changeUserGrade(id, grade);
    load();
  };

  return (
    <div>
      <h2 style={styles.title}>회원 관리</h2>

      {loading ? <Loading /> : (
        <table style={styles.table}>
          <thead>
            <tr style={styles.thead}>
              {['ID', '이름', '이메일', '전화번호', '권한', '상태', '등급', '가입일', '관리'].map(h => (
                <th key={h} style={styles.th}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {users.map(user => (
              <tr key={user.id} style={styles.tr}>
                <td style={styles.td}>{user.id}</td>
                <td style={styles.td}>{user.name}</td>
                <td style={styles.td}>{user.email}</td>
                <td style={styles.td}>{user.phone || '-'}</td>
                <td style={styles.td}>
                  <span style={user.role === 'ADMIN' ? styles.adminBadge : styles.userBadge}>
                    {ROLE_LABEL[user.role] || user.role}
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={styles.statusBadge[user.status] || styles.statusBadge.ACTIVE}>
                    {STATUS_LABEL[user.status] || user.status}
                  </span>
                </td>
                <td style={styles.td}>
                  <span style={user.grade === 'BLACK' ? styles.blackBadge : styles.normalBadge}>
                    {GRADE_LABEL[user.grade] || user.grade}
                  </span>
                </td>
                <td style={styles.td}>{user.createdAt?.slice(0, 10)}</td>
                <td style={styles.td}>
                  <div style={styles.actions}>
                    {user.grade !== 'BLACK' ? (
                      <button style={styles.blackBtn} onClick={() => handleGrade(user.id, 'BLACK', '블랙등급 지정')}>
                        블랙지정
                      </button>
                    ) : (
                      <button style={styles.unblackBtn} onClick={() => handleGrade(user.id, 'NORMAL', '블랙등급 해제')}>
                        블랙해제
                      </button>
                    )}
                    {user.status === 'ACTIVE' && (
                      <button style={styles.suspendBtn} onClick={() => handleStatus(user.id, 'SUSPENDED', '정지')}>
                        정지
                      </button>
                    )}
                    {user.status === 'SUSPENDED' && (
                      <button style={styles.activateBtn} onClick={() => handleStatus(user.id, 'ACTIVE', '활성화')}>
                        활성화
                      </button>
                    )}
                    {user.status !== 'DELETED' && (
                      <button style={styles.deleteBtn} onClick={() => handleStatus(user.id, 'DELETED', '탈퇴')}>
                        탈퇴처리
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {totalPages > 1 && (
        <div style={styles.pagination}>
          {Array.from({ length: totalPages }, (_, i) => (
            <button key={i} style={{ ...styles.pageBtn, ...(page === i ? styles.pageBtnActive : {}) }} onClick={() => setPage(i)}>
              {i + 1}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

const styles: Record<string, any> = {
  title: { fontSize: 22, fontWeight: 700, marginBottom: 24 },
  table: { width: '100%', borderCollapse: 'collapse', background: '#fff', borderRadius: 12, overflow: 'hidden', border: '1px solid #f0f0f0' },
  thead: { background: '#fafafa' },
  th: { padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 600, color: '#888', borderBottom: '1px solid #f0f0f0' },
  tr: { borderBottom: '1px solid #f9f9f9' },
  td: { padding: '12px 16px', fontSize: 13, color: '#333', verticalAlign: 'middle' },
  adminBadge: { padding: '3px 8px', borderRadius: 10, background: '#f3e5f5', color: '#9c27b0', fontSize: 11, fontWeight: 600 },
  userBadge: { padding: '3px 8px', borderRadius: 10, background: '#e3f2fd', color: '#1976d2', fontSize: 11, fontWeight: 600 },
  statusBadge: {
    ACTIVE:    { padding: '3px 10px', borderRadius: 10, background: '#e8f5e9', color: '#4caf50', fontSize: 11, fontWeight: 600 },
    SUSPENDED: { padding: '3px 10px', borderRadius: 10, background: '#fff3e0', color: '#ff9800', fontSize: 11, fontWeight: 600 },
    DELETED:   { padding: '3px 10px', borderRadius: 10, background: '#ffebee', color: '#e53935', fontSize: 11, fontWeight: 600 },
  },
  normalBadge: { padding: '3px 10px', borderRadius: 10, background: '#f5f5f5', color: '#757575', fontSize: 11, fontWeight: 600 },
  blackBadge: { padding: '3px 10px', borderRadius: 10, background: '#212121', color: '#fff', fontSize: 11, fontWeight: 600 },
  actions: { display: 'flex', gap: 6 },
  blackBtn: { padding: '4px 10px', background: '#212121', color: '#fff', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  unblackBtn: { padding: '4px 10px', background: '#f5f5f5', color: '#757575', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  suspendBtn: { padding: '4px 10px', background: '#fff3e0', color: '#ff9800', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  activateBtn: { padding: '4px 10px', background: '#e8f5e9', color: '#4caf50', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  deleteBtn: { padding: '4px 10px', background: '#ffebee', color: '#e53935', border: 'none', borderRadius: 6, fontSize: 11, fontWeight: 600, cursor: 'pointer' },
  pagination: { display: 'flex', justifyContent: 'center', gap: 8, marginTop: 20 },
  pageBtn: { width: 32, height: 32, borderRadius: 6, border: '1px solid #e8e8e8', background: '#fff', cursor: 'pointer' },
  pageBtnActive: { background: '#b39ddb', color: '#fff', borderColor: '#b39ddb' },
};

export default AdminUsers;
